import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { NAV_SECTIONS, ADMIN_SECTION } from "./nav/navConfig";
import Sidebar from "./nav/Sidebar";
import Navbar from "./nav/Navbar";
import BottomTabBar from "./nav/BottomTabBar";

export default function Layout() {
  const { user, logout } = useAuth();
  const { brand } = useBranding();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [navQuery, setNavQuery] = useState("");
  const [badges, setBadges] = useState({ messages: 0, incidents: 0, announcements: 0, tasks_overdue: 0 });
  const [dragLabel, setDragLabel] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [prefsHydrated, setPrefsHydrated] = useState(false);

  // Persisted per-browser preferences: section order + which sections are collapsed.
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("nav.order") || "null");
      if (Array.isArray(saved)) return saved;
    } catch (e) { console.debug("nav.order parse failed:", e?.message); }
    return NAV_SECTIONS.map((s) => s.label);
  });
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("nav.collapsed") || "null");
      if (saved && typeof saved === "object") return saved;
    } catch (e) { console.debug("nav.collapsed parse failed:", e?.message); }
    return {};
  });
  useEffect(() => { try { localStorage.setItem("nav.order", JSON.stringify(order)); } catch (e) { console.debug("nav.order write failed:", e?.message); } }, [order]);
  useEffect(() => { try { localStorage.setItem("nav.collapsed", JSON.stringify(collapsed)); } catch (e) { console.debug("nav.collapsed write failed:", e?.message); } }, [collapsed]);

  // Hydrate prefs from server on mount — server wins over localStorage so devices stay in sync.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/users/me/prefs").then(({ data }) => {
      if (cancelled) return;
      if (Array.isArray(data?.nav_order) && data.nav_order.length) setOrder(data.nav_order);
      if (data?.nav_collapsed && typeof data.nav_collapsed === "object") setCollapsed(data.nav_collapsed);
    }).catch((e) => console.debug("prefs hydrate failed:", e?.message)).finally(() => { if (!cancelled) setPrefsHydrated(true); });
    return () => { cancelled = true; };
  }, [user]);

  // Sync order + collapsed changes to server (debounced, only after hydration to avoid clobbering).
  useEffect(() => {
    if (!user || !prefsHydrated) return;
    const t = setTimeout(() => {
      api.put("/users/me/prefs", { nav_order: order, nav_collapsed: collapsed }).catch((e) => console.debug("prefs sync failed:", e?.message));
    }, 600);
    return () => clearTimeout(t);
  }, [order, collapsed, prefsHydrated, user]);

  // Build the ordered section list — Admin is always pinned last for admin users.
  const sections = useMemo(() => {
    const rawMap = Object.fromEntries(NAV_SECTIONS.map((s) => [s.label, s]));
    if (user?.role === "admin") rawMap[ADMIN_SECTION.label] = ADMIN_SECTION;
    const map = Object.fromEntries(Object.entries(rawMap).map(([label, s]) => [label, {
      ...s,
      items: s.items.filter((i) => !i.roles || (user?.role && i.roles.includes(user.role))),
    }]));
    const known = Object.keys(map);
    const ordered = [
      ...order.filter((l) => known.includes(l) && l !== ADMIN_SECTION.label),
      ...known.filter((l) => !order.includes(l) && l !== ADMIN_SECTION.label),
    ];
    if (user?.role === "admin") ordered.push(ADMIN_SECTION.label);
    return ordered.map((l) => map[l]).filter((s) => s.items.length > 0);
  }, [order, user]);

  const query = navQuery.trim().toLowerCase();
  const filteredSections = useMemo(() => (
    query
      ? sections
          .map((s) => ({ ...s, items: s.items.filter((i) => i.label.toLowerCase().includes(query)) }))
          .filter((s) => s.items.length > 0)
      : sections
  ), [sections, query]);
  const anyMatch = filteredSections.length > 0;

  const toggleCollapse = useCallback((label) => setCollapsed((c) => ({ ...c, [label]: !c[label] })), []);

  const onDragStart = useCallback((label) => (e) => {
    if (query || label === ADMIN_SECTION.label) { e.preventDefault(); return; }
    setDragLabel(label);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", label);
  }, [query]);
  const onDragOver = useCallback((label) => (e) => {
    if (!dragLabel || dragLabel === label || label === ADMIN_SECTION.label) return;
    e.preventDefault();
    setDropTarget(label);
  }, [dragLabel]);
  const onDrop = useCallback((label) => (e) => {
    e.preventDefault();
    if (!dragLabel || dragLabel === label || label === ADMIN_SECTION.label) return;
    setOrder((cur) => {
      const base = cur.filter((l) => l !== ADMIN_SECTION.label);
      const from = base.indexOf(dragLabel);
      const to = base.indexOf(label);
      if (from < 0 || to < 0) return cur;
      const next = [...base];
      next.splice(from, 1);
      next.splice(to, 0, dragLabel);
      return next;
    });
    setDragLabel(null); setDropTarget(null);
  }, [dragLabel]);
  const onDragEnd = useCallback(() => { setDragLabel(null); setDropTarget(null); }, []);

  // Presence heartbeat — keeps the little green dot in the Directory alive.
  useEffect(() => {
    if (!user) return;
    const beat = () => api.post("/presence/heartbeat").catch((e) => console.debug("presence heartbeat failed:", e?.message));
    beat();
    const t = setInterval(beat, 20000);
    return () => clearInterval(t);
  }, [user]);

  // Poll nav badges (unread messages + open incidents) every 15s.
  useEffect(() => {
    if (!user) return;
    const load = () => api.get("/nav-badges").then(({ data }) => setBadges(data || {})).catch((e) => console.debug("nav-badges poll failed:", e?.message));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); setNavQuery(""); }, [location.pathname]);

  const handleLogout = useCallback(async () => {
    await logout();
    nav("/login");
  }, [logout, nav]);

  return (
    <div className="min-h-screen flex bg-[#0b0d12] text-slate-100">
      {drawerOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setDrawerOpen(false)}
          data-testid="drawer-scrim"
          className="lg:hidden fixed inset-0 z-30 bg-black/70 backdrop-blur-md animate-fade-in"
        />
      )}

      <Sidebar
        brand={brand}
        drawerOpen={drawerOpen}
        onCloseDrawer={() => setDrawerOpen(false)}
        navQuery={navQuery}
        setNavQuery={setNavQuery}
        filteredSections={filteredSections}
        anyMatch={anyMatch}
        collapsed={collapsed}
        toggleCollapse={toggleCollapse}
        badges={badges}
        onLogout={handleLogout}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        dragLabel={dragLabel}
        dropTarget={dropTarget}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Navbar brand={brand} user={user} onOpenDrawer={() => setDrawerOpen(true)} />
        <main className="flex-1 overflow-auto bg-hero-radial">
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto pb-24 sm:pb-8">
            <Outlet />
          </div>
        </main>
        <BottomTabBar />
      </div>
    </div>
  );
}
